import type { VisitDto } from '@aawas/contracts';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Delete, DoorOpen, KeyRound, LogOut, Phone, Plus, RefreshCw, ShieldAlert, UserX, WifiOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { enqueue, flushOutbox, loadSnapshot, newKey, refreshSnapshot, useOutbox, verifyPassOffline, type Snapshot } from '../lib/offline';
import { useVisitAction, useVisits } from '../lib/queries';
import { useRealtime } from '../lib/realtime';
import { useSession } from '../lib/session';
import { Badge, Button, Card, Empty, Field, Input, KIND_LABEL, PageHeader, Select, Sheet, Spinner, StatusBadge, VISIT_TONE, timeAgo } from '../ui';
import { useToast } from '../ui/toast';
import { VisitTitle } from './visits';

// ───────────── alert helpers ─────────────

function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

function beep(times = 3) {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    for (let i = 0; i < times; i++) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'square';
      o.frequency.value = 880;
      g.gain.value = 0.25;
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * 0.3);
      o.stop(ctx.currentTime + i * 0.3 + 0.18);
    }
    setTimeout(() => void ctx.close(), times * 300 + 300);
  } catch {
    /* audio blocked until first user gesture */
  }
  navigator.vibrate?.([300, 120, 300, 120, 300]);
}

const ACK_KEY = 'gate.ackedStops';
const loadAcked = (): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(ACK_KEY) ?? '[]'));
  } catch {
    return new Set();
  }
};

/**
 * Full-screen, unmissable "do not admit" alert. Driven by the visit data itself (not just the socket
 * message), so it appears even if a realtime event was missed, and stays until the guard acknowledges.
 */
function StopOverlay({ visit, onAck }: { visit: VisitDto; onAck: () => void }) {
  useEffect(() => {
    beep();
    const t = setInterval(() => beep(2), 6000);
    return () => clearInterval(t);
  }, [visit.id]);
  return (
    <div role="alertdialog" aria-modal="true" aria-label="Do not allow entry" className="stop-pulse fixed inset-0 z-[70] flex flex-col items-center justify-center gap-5 p-6 text-center text-white">
      <ShieldAlert className="h-24 w-24" aria-hidden />
      <div className="text-6xl font-black tracking-tight">STOP</div>
      <div className="text-2xl font-bold">Flat {visit.unitLabel} rejected</div>
      <div className="text-3xl font-extrabold">{visit.visitorName}</div>
      <div className="text-lg opacity-90">{KIND_LABEL[visit.kind]}{visit.company ? ` · ${visit.company}` : ''} — <b>do not allow entry</b></div>
      {visit.decisionNote && <div className="max-w-sm rounded-xl bg-black/25 px-4 py-2 text-base">"{visit.decisionNote}"</div>}
      <Button size="lg" variant="secondary" className="mt-4 min-w-64 !text-rose-800" onClick={onAck}>I understand — turn them away</Button>
    </div>
  );
}

// ───────────── console ─────────────

export function GuardConsole() {
  const { current } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const { connected } = useRealtime();
  const now = useNow();
  const visits = useVisits({ open: true, limit: 100 }, connected ? 10_000 : 3_000);
  const act = useVisitAction();
  const outbox = useOutbox();
  const [snap, setSnap] = useState<Snapshot>();
  const [acked, setAcked] = useState(loadAcked);
  const [newOpen, setNewOpen] = useState(false);
  const [passOpen, setPassOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const lastCount = useRef(0);

  // Keep the offline snapshot fresh and replay any queued actions.
  useEffect(() => {
    void loadSnapshot().then(setSnap);
    const refresh = () => void refreshSnapshot().then(setSnap);
    refresh();
    const t = setInterval(refresh, 5 * 60_000);
    return () => clearInterval(t);
  }, []);

  const sync = useCallback(async () => {
    setSyncing(true);
    try {
      const r = await flushOutbox();
      if (r.sent) void qc.invalidateQueries({ queryKey: ['visits'] });
      for (const d of r.dropped) toast.show({ title: `Could not sync: ${d.label}`, body: d.reason, tone: 'error' });
    } finally {
      setSyncing(false);
    }
  }, [qc, toast]);

  useEffect(() => {
    void sync();
    const t = setInterval(() => void sync(), 15_000);
    window.addEventListener('online', sync);
    return () => {
      clearInterval(t);
      window.removeEventListener('online', sync);
    };
  }, [sync]);

  const all = visits.data?.items ?? [];
  const waiting = all.filter((v) => v.status === 'pending');
  const ready = all.filter((v) => v.status === 'approved');
  const noAnswer = all.filter((v) => v.status === 'no_response');
  const rejected = all.filter((v) => v.status === 'rejected');
  const inside = all.filter((v) => v.status === 'entered');

  // A rejection newer than 30 minutes that has not been acknowledged on this device.
  const stop = rejected.find((v) => !acked.has(v.id) && v.decidedAt && Date.now() - new Date(v.decidedAt).getTime() < 30 * 60_000);
  const ack = (id: string) => {
    const next = new Set(acked).add(id);
    setAcked(next);
    try { localStorage.setItem(ACK_KEY, JSON.stringify([...next].slice(-200))); } catch { /* ignore */ }
  };

  // Chime when a new visitor decision needs the guard's attention.
  const attention = ready.length + noAnswer.length;
  useEffect(() => {
    if (attention > lastCount.current) beep(2);
    lastCount.current = attention;
  }, [attention]);

  const residentsOf = (unitId: string) => snap?.units.find((u) => u.id === unitId)?.residents ?? [];

  async function doAction(v: VisitDto, action: 'enter' | 'exit' | 'cancel') {
    try {
      await act.mutateAsync({ id: v.id, action });
    } catch (e) {
      if (e instanceof ApiError && e.isNetwork) {
        await enqueue({ id: newKey(), kind: action, visitId: v.id, label: `${action} · ${v.visitorName}`, at: Date.now() });
        toast.show({ title: 'Saved offline', body: 'It will sync when the connection returns.', tone: 'alert' });
      } else if (e instanceof ApiError && e.code === 'VISIT_REJECTED') {
        ack(v.id);
        toast.show({ title: 'Rejected by resident', body: 'Do not allow entry. Ask the committee if this is urgent.', tone: 'error' });
      } else toast.error(e);
    }
  }

  return (
    <div className="space-y-5">
      {stop && <StopOverlay visit={stop} onAck={() => { ack(stop.id); void doAction(stop, 'cancel'); }} />}

      <PageHeader title="Gate" subtitle={current.communityName} />

      {(!connected || outbox.length > 0) && (
        <div role="status" className={clsx('flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm font-medium', connected ? 'bg-amber-100 text-amber-900' : 'bg-slate-800 text-white')}>
          <span className="flex items-center gap-2"><WifiOff className="h-4 w-4" aria-hidden />{connected ? `${outbox.length} action${outbox.length > 1 ? 's' : ''} waiting to sync` : `Offline${outbox.length ? ` · ${outbox.length} queued` : ''} — the gate still works`}</span>
          <button className="flex items-center gap-1 font-semibold underline" onClick={() => void sync()} disabled={syncing}><RefreshCw className={clsx('h-4 w-4', syncing && 'animate-spin')} aria-hidden />Sync</button>
        </div>
      )}
      {outbox.length > 0 && (
        <ul className="space-y-1 text-sm text-slate-600" aria-label="Queued actions">
          {outbox.map((o) => <li key={o.id} className="flex items-center gap-2"><Badge tone="amber">Queued</Badge>{o.label}</li>)}
        </ul>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Button size="lg" className="h-20 flex-col !text-lg" onClick={() => setNewOpen(true)}><Plus className="h-7 w-7" aria-hidden />New visitor</Button>
        <Button size="lg" variant="secondary" className="h-20 flex-col !text-lg" onClick={() => setPassOpen(true)}><KeyRound className="h-7 w-7" aria-hidden />Pass code</Button>
      </div>

      {visits.isLoading && <Spinner />}

      {ready.length > 0 && (
        <Section title="Approved — let in" tone="green">
          {ready.map((v) => (
            <GateCard key={v.id} v={v} residents={residentsOf(v.unitId)}>
              <Button variant="success" size="lg" className="w-full" loading={act.isPending} onClick={() => doAction(v, 'enter')}><DoorOpen className="h-5 w-5" aria-hidden />Let in</Button>
            </GateCard>
          ))}
        </Section>
      )}

      {noAnswer.length > 0 && (
        <Section title="No answer" tone="amber">
          {noAnswer.map((v) => (
            <GateCard key={v.id} v={v} residents={residentsOf(v.unitId)}>
              <p className="mb-2 text-sm text-amber-900">Nobody at flat {v.unitLabel} answered. Call them, or use your judgement.</p>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="secondary" onClick={() => doAction(v, 'cancel')}><UserX className="h-4 w-4" aria-hidden />Turn away</Button>
                <Button variant="success" onClick={() => doAction(v, 'enter')}>Let in anyway</Button>
              </div>
            </GateCard>
          ))}
        </Section>
      )}

      {waiting.length > 0 && (
        <Section title="Waiting for resident" tone="amber">
          {waiting.map((v) => (
            <GateCard key={v.id} v={v} residents={residentsOf(v.unitId)} now={now}>
              <Button variant="secondary" size="sm" onClick={() => doAction(v, 'cancel')}>Cancel request</Button>
            </GateCard>
          ))}
        </Section>
      )}

      {rejected.filter((v) => acked.has(v.id) || !v.decidedAt).length > 0 && (
        <Section title="Rejected — turn away" tone="red">
          {rejected.filter((v) => acked.has(v.id)).map((v) => (
            <GateCard key={v.id} v={v} residents={residentsOf(v.unitId)}>
              <Button variant="secondary" onClick={() => doAction(v, 'cancel')}>Clear</Button>
            </GateCard>
          ))}
        </Section>
      )}

      {inside.length > 0 && (
        <Section title={`Inside now (${inside.length})`} tone="blue">
          {inside.map((v) => (
            <GateCard key={v.id} v={v} residents={[]} compact>
              <Button variant="secondary" size="sm" onClick={() => doAction(v, 'exit')}><LogOut className="h-4 w-4" aria-hidden />Left</Button>
            </GateCard>
          ))}
        </Section>
      )}

      {!visits.isLoading && all.length === 0 && outbox.length === 0 && <Empty icon={<DoorOpen className="h-8 w-8" />} title="Gate is quiet" hint="Log a visitor with “New visitor”, or enter a guest's pass code." />}

      <NewVisitorSheet open={newOpen} onClose={() => setNewOpen(false)} snap={snap} />
      <PassCodeSheet open={passOpen} onClose={() => setPassOpen(false)} snap={snap} />
    </div>
  );
}

function Section({ title, tone, children }: { title: string; tone: 'green' | 'amber' | 'red' | 'blue'; children: React.ReactNode }) {
  const c = { green: 'text-emerald-800', amber: 'text-amber-800', red: 'text-rose-800', blue: 'text-blue-800' }[tone];
  return (
    <section className="space-y-2" aria-label={title}>
      <h2 className={clsx('text-sm font-bold uppercase tracking-wide', c)}>{title}</h2>
      {children}
    </section>
  );
}

function GateCard({ v, residents, children, now, compact }: { v: VisitDto; residents: Snapshot['units'][number]['residents']; children: React.ReactNode; now?: number; compact?: boolean }) {
  const left = v.decisionDeadline && now ? Math.max(0, Math.round((new Date(v.decisionDeadline).getTime() - now) / 1000)) : null;
  return (
    <Card className={clsx(!compact && 'space-y-3')}>
      <div className="flex items-start justify-between gap-2">
        <VisitTitle v={v} />
        <div className="shrink-0 text-right">
          <StatusBadge map={VISIT_TONE} status={v.status} />
          <div className="mt-1 text-xs text-slate-500">{left !== null ? (left > 0 ? `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}` : 'timed out') : v.status === 'entered' && v.enteredAt ? `entered ${timeAgo(v.enteredAt)}` : timeAgo(v.createdAt)}</div>
        </div>
      </div>
      {residents.length > 0 && (v.status === 'pending' || v.status === 'no_response') && (
        <div className="flex flex-wrap gap-2">
          {residents.slice(0, 3).map((r) => (
            <a key={r.phone} href={`tel:${r.phone}`} className="inline-flex min-h-9 items-center gap-1.5 rounded-full bg-slate-100 px-3 text-sm font-medium text-slate-800 active:bg-slate-200"><Phone className="h-4 w-4" aria-hidden />{r.name ?? r.phone}</a>
          ))}
        </div>
      )}
      <div className={clsx(compact && 'mt-2')}>{children}</div>
    </Card>
  );
}

// ───────────── new visitor ─────────────

function NewVisitorSheet({ open, onClose, snap }: { open: boolean; onClose: () => void; snap?: Snapshot }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [unitQuery, setUnitQuery] = useState('');
  const [unitId, setUnitId] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'guest' | 'delivery' | 'cab' | 'service'>('guest');
  const [company, setCompany] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  const matches = useMemo(() => {
    const q = unitQuery.trim().toLowerCase();
    if (!q || !snap) return [];
    return snap.units.filter((u) => u.label.toLowerCase().includes(q)).slice(0, 6);
  }, [unitQuery, snap]);
  const unit = snap?.units.find((u) => u.id === unitId);

  const reset = () => { setUnitQuery(''); setUnitId(''); setName(''); setKind('guest'); setCompany(''); setVehicle(''); setPhone(''); };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!unitId || !name.trim()) return;
    setBusy(true);
    const body = { unitId, visitorName: name.trim(), kind, idempotencyKey: newKey(), ...(company.trim() && { company: company.trim() }), ...(vehicle.trim() && { vehicleNo: vehicle.trim() }), ...(phone.trim() && { visitorPhone: phone.trim() }) };
    try {
      await api<VisitDto>('/visits', { body });
      toast.success(`Asking flat ${unit?.label}…`);
      void qc.invalidateQueries({ queryKey: ['visits'] });
      reset();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.isNetwork) {
        await enqueue({ id: newKey(), kind: 'visit', body, label: `${name.trim()} → ${unit?.label}`, at: Date.now() });
        toast.show({ title: 'Saved offline', body: 'The resident will be asked as soon as you are back online. Call them meanwhile.', tone: 'alert' });
        reset();
        onClose();
      } else toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="New visitor">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Flat number">
          {unit ? (
            <div className="flex items-center justify-between rounded-xl bg-white px-3.5 py-3 ring-1 ring-brand-500">
              <span className="text-lg font-bold">{unit.label}</span>
              <button type="button" className="text-sm font-semibold text-brand-700" onClick={() => { setUnitId(''); setUnitQuery(''); }}>Change</button>
            </div>
          ) : (
            <>
              <Input autoFocus inputMode="text" autoCapitalize="characters" placeholder="e.g. A-101" value={unitQuery} onChange={(e) => setUnitQuery(e.target.value)} />
              {matches.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {matches.map((u) => <button type="button" key={u.id} onClick={() => setUnitId(u.id)} className="min-h-11 rounded-xl bg-white px-4 text-lg font-bold ring-1 ring-slate-300 active:bg-brand-50">{u.label}</button>)}
                </div>
              )}
              {unitQuery && matches.length === 0 && <p className="mt-1 text-sm text-rose-700">{snap ? 'No such flat' : 'Loading flats…'}</p>}
            </>
          )}
        </Field>

        <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label="Visitor type">
          {(['guest', 'delivery', 'cab', 'service'] as const).map((k) => (
            <button type="button" key={k} role="radio" aria-checked={kind === k} onClick={() => setKind(k)} className={clsx('min-h-12 rounded-xl text-sm font-semibold ring-1', kind === k ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-700 ring-slate-300')}>{KIND_LABEL[k]}</button>
          ))}
        </div>

        <Field label={kind === 'delivery' ? 'Delivery person' : 'Visitor name'}><Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} placeholder="Name" /></Field>
        {(kind === 'delivery' || kind === 'service' || kind === 'cab') && <Field label={kind === 'delivery' ? 'Company (Swiggy, Amazon…)' : 'Company / agency'}><Input value={company} onChange={(e) => setCompany(e.target.value)} maxLength={60} /></Field>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vehicle no."><Input value={vehicle} onChange={(e) => setVehicle(e.target.value.toUpperCase())} maxLength={20} placeholder="KA01AB1234" /></Field>
          <Field label="Phone"><Input inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="optional" /></Field>
        </div>
        <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!unitId || !name.trim()}>Ask resident</Button>
      </form>
    </Sheet>
  );
}

// ───────────── pass code ─────────────

function PassCodeSheet({ open, onClose, snap }: { open: boolean; onClose: () => void; snap?: Snapshot }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ name: string; unit: string; offline: boolean } | null>(null);

  const close = () => { setCode(''); setResult(null); onClose(); };
  const press = (d: string) => setCode((c) => (c.length < 6 ? c + d : c));

  async function submit() {
    if (code.length !== 6) return;
    setBusy(true);
    const key = newKey();
    try {
      const v = await api<VisitDto>('/passes/check-in', { body: { code, idempotencyKey: key } });
      setResult({ name: v.visitorName, unit: v.unitLabel, offline: false });
      void qc.invalidateQueries({ queryKey: ['visits'] });
    } catch (e) {
      if (e instanceof ApiError && e.isNetwork) {
        const p = verifyPassOffline(snap, code);
        if (p) {
          await enqueue({ id: newKey(), kind: 'checkin', body: { code, idempotencyKey: key }, label: `Pass ${code} · ${p.visitorName}`, at: Date.now() });
          setResult({ name: p.visitorName, unit: p.unitLabel, offline: true });
        } else toast.show({ title: 'Cannot verify offline', body: 'Not in the saved pass list. Ask the resident to approve by phone.', tone: 'error' });
      } else if (e instanceof ApiError) {
        toast.show({ title: e.code === 'PASS_INVALID' ? 'Invalid pass code' : e.message, body: e.code === 'PASS_INVALID' ? 'Not found, already used, expired or cancelled.' : undefined, tone: 'error' });
      } else toast.error(e);
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onClose={close} title="Guest pass code">
      {result ? (
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-4xl">✓</div>
          <div className="text-2xl font-bold">{result.name}</div>
          <div className="text-lg text-slate-700">Allowed into flat <b>{result.unit}</b></div>
          {result.offline && <Badge tone="amber">Verified offline — will sync</Badge>}
          <Button size="lg" className="w-full" onClick={close}>Done</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-center gap-2" aria-live="polite" aria-label={`Code entered: ${code.length} of 6 digits`}>
            {Array.from({ length: 6 }, (_, i) => <div key={i} className="flex h-14 w-11 items-center justify-center rounded-xl bg-white text-3xl font-bold ring-1 ring-slate-300">{code[i] ?? ''}</div>)}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => <button key={d} type="button" onClick={() => press(d)} className="min-h-16 rounded-2xl bg-white text-2xl font-semibold ring-1 ring-slate-300 active:bg-brand-50">{d}</button>)}
            <button type="button" onClick={() => setCode('')} className="min-h-16 rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700 active:bg-slate-200">Clear</button>
            <button type="button" onClick={() => press('0')} className="min-h-16 rounded-2xl bg-white text-2xl font-semibold ring-1 ring-slate-300 active:bg-brand-50">0</button>
            <button type="button" aria-label="Delete last digit" onClick={() => setCode((c) => c.slice(0, -1))} className="flex min-h-16 items-center justify-center rounded-2xl bg-slate-100 active:bg-slate-200"><Delete className="h-6 w-6" /></button>
          </div>
          <Button size="lg" className="w-full" loading={busy} disabled={code.length !== 6} onClick={submit}>Check pass</Button>
        </div>
      )}
    </Sheet>
  );
}

// Guards use the shared visitors screen as their log.
export { VisitorsScreen as GateLogScreen } from './visits';
